import React, { useEffect, useState } from 'react';
import { useRaceStore } from '../../stores/raceStore';
import { useTranslation } from '../../utils/i18n';
import { fetchWeatherForecast, WeatherForecastData, getWeatherDescriptionKey, getWeatherEmoji } from '../../utils/weatherApi';
import { CloudRainIcon, WindIcon, DropletIcon, AlertTriangleIcon, ThermometerIcon, SnowflakeIcon, ZapIcon, Loader2Icon, CloudOffIcon } from 'lucide-react';
const ALERT_ICONS: Record<string, React.ReactNode> = {
  storm: <ZapIcon className="h-3.5 w-3.5" data-id="element-462" />,
  wind: <WindIcon className="h-3.5 w-3.5" data-id="element-463" />,
  rain: <CloudRainIcon className="h-3.5 w-3.5" data-id="element-464" />,
  snow: <SnowflakeIcon className="h-3.5 w-3.5" data-id="element-465" />,
  cold: <ThermometerIcon className="h-3.5 w-3.5" data-id="element-466" />,
  heat: <ThermometerIcon className="h-3.5 w-3.5" data-id="element-467" />
};
export function WeatherForecast() {
  const {
    t,
    locale
  } = useTranslation();
  const {
    gpxData,
    settings,
    pacingResults
  } = useRaceStore();
  const [forecast, setForecast] = useState<WeatherForecastData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!gpxData || gpxData.points.length === 0 || !settings.startTime) return;
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      const firstPoint = gpxData.points[0];
      const startTime = new Date(settings.startTime);
      // Estimate race duration from pacing or default to 24h
      let durationHours = 24;
      if (pacingResults.length > 0) {
        const lastResult = pacingResults[pacingResults.length - 1];
        durationHours = Math.ceil(lastResult.accumulatedTime / 60) + 1; // +1h buffer
      }
      const result = await fetchWeatherForecast(firstPoint.lat, firstPoint.lon, startTime, Math.min(durationHours, 120));
      if (result) {
        setForecast(result);
      } else {
        setError(true);
      }
      setLoading(false);
    };
    fetchData();
  }, [gpxData?.points[0]?.lat, settings.startTime, pacingResults.length]);
  if (!gpxData || !settings.startTime) return null;
  if (loading) {
    return <div className="p-6 flex items-center justify-center gap-3" data-id="element-468">
        <Loader2Icon className="h-5 w-5 text-[#FC4C02] animate-spin" data-id="element-469" />
        <span className="text-sm font-medium text-[#6B6B76]" data-id="element-470">
          {t('weather.loading')}
        </span>
      </div>;
  }
  if (error || !forecast) {
    return <div className="p-6 flex items-center justify-center gap-3" data-id="element-471">
        <CloudOffIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-472" />
        <span className="text-sm font-medium text-[#9B9BA7]" data-id="element-473">
          {t('weather.unavailable')}
        </span>
      </div>;
  }
  const startDate = new Date(settings.startTime);
  const dateStr = startDate.toLocaleDateString(locale === 'pt' ? 'pt-PT' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return <div data-id="element-474">
      {/* Subtitle */}
      <div className="px-4 lg:px-5 pt-3 pb-2" data-id="element-475">
        <p className="text-xs text-[#9B9BA7]" data-id="element-476">
          {t('weather.subtitle', {
          date: dateStr
        })}
        </p>
      </div>

      {/* Alerts */}
      {forecast.alerts.length > 0 && <div className="px-4 lg:px-5 pb-3 space-y-1.5" data-id="element-477">
          {forecast.alerts.map((alert, idx) => <div key={idx} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold ${alert.severity === 'danger' ? 'bg-[#E34402]/10 text-[#E34402] border border-[#E34402]/20' : 'bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/20'}`} data-id="element-478">
              {alert.severity === 'danger' ? <AlertTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" data-id="element-479" /> : <span className="flex-shrink-0" data-id="element-480">{ALERT_ICONS[alert.type]}</span>}
              {alert.messageKey ? t(alert.messageKey, alert.messageValue ? {
          value: alert.messageValue
        } : undefined) : alert.message}
            </div>)}
        </div>}

      {/* Time Blocks */}
      <div className="px-4 lg:px-5 pb-4 lg:pb-5" data-id="element-481">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-482">
          {forecast.blocks.map((block, idx) => {
          const hasPrecip = block.totalPrecip > 0.5;
          const hasStrongWind = block.maxWind >= 25;
          return <div key={idx} className="rounded-xl border border-[#E8E8ED] bg-[#FAFAFA] p-3.5" data-id="element-483">
                {/* Block header */}
                <div className="text-center mb-3" data-id="element-484">
                  <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-485">
                    {block.dateLabel}
                  </div>
                  <div className="text-xs font-bold text-[#2D2D2D]" data-id="element-486">
                    {t(`weather.block.${block.label}`)}
                  </div>
                </div>

                {/* Weather icon + condition + temp */}
                <div className="flex items-center justify-between gap-1.5 mb-3" data-id="element-487">
                  <div className="flex items-center gap-1.5 min-w-0" data-id="element-488">
                    <span className="text-xl sm:text-2xl leading-none flex-shrink-0" data-id="element-489">
                      {getWeatherEmoji(block.dominantCode)}
                    </span>
                    <div className="min-w-0" data-id="element-490">
                      <div className="text-[9px] sm:text-[10px] font-semibold text-[#6B6B76] leading-tight truncate" data-id="element-491">
                        {t(getWeatherDescriptionKey(block.dominantCode))}
                      </div>
                    </div>
                  </div>
                  <div className="text-lg sm:text-xl font-bold text-[#2D2D2D] tabular-nums flex-shrink-0" data-id="element-492">
                    {block.avgTemp.toFixed(1)}°
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 gap-2" data-id="element-493">
                  <div className={`rounded-lg px-2 py-1.5 text-center ${hasStrongWind ? 'bg-[#FF9500]/10' : 'bg-white'}`} data-id="element-494">
                    <WindIcon className={`h-3 w-3 mx-auto mb-0.5 ${hasStrongWind ? 'text-[#FF9500]' : 'text-[#9B9BA7]'}`} data-id="element-495" />
                    <div className="text-[10px] text-[#9B9BA7]" data-id="element-496">
                      {t('weather.wind')}
                    </div>
                    <div className={`text-[11px] font-bold tabular-nums ${hasStrongWind ? 'text-[#FF9500]' : 'text-[#2D2D2D]'}`} data-id="element-497">
                      {block.maxWind.toFixed(1)} km/h
                    </div>
                  </div>
                  <div className={`rounded-lg px-2 py-1.5 text-center ${hasPrecip ? 'bg-[#0666EB]/10' : 'bg-white'}`} data-id="element-498">
                    <DropletIcon className={`h-3 w-3 mx-auto mb-0.5 ${hasPrecip ? 'text-[#0666EB]' : 'text-[#9B9BA7]'}`} data-id="element-499" />
                    <div className="text-[10px] text-[#9B9BA7]" data-id="element-500">
                      {hasPrecip ? t('weather.precip') : t('weather.humidity')}
                    </div>
                    <div className={`text-[11px] font-bold tabular-nums ${hasPrecip ? 'text-[#0666EB]' : 'text-[#2D2D2D]'}`} data-id="element-501">
                      {hasPrecip ? `${block.totalPrecip.toFixed(1)} mm` : `${Math.round(block.avgHumidity)}%`}
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}